import { CaptchaInput } from "@/components/fields/CaptchaInput";
import { Alert } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { InputGroup } from "@/components/ui/input-group";
import { infoService } from "@/service/InfoService";
import {
  Box,
  Fieldset,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  LuCircleCheck,
  LuClock3,
  LuMail,
  LuMessageSquare,
  LuSend,
  LuUserRound,
} from "react-icons/lu";
import { useI18n } from "src/i18n/I18nContext";
import { trackGoogleAnalyticsEvent } from "src/analytics/googleAnalytics";

export interface ContactMessage {
  name: string;
  email: string;
  message: string;
  captcha: string;
}

const Contact = () => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const topic = searchParams.get("topic");
  const initialMessage = topic === "demo"
    ? t("contact.demoMessage")
    : topic === "security"
      ? t("contact.securityMessage")
      : "";
  const {
    register,
    handleSubmit,
    reset,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<ContactMessage>({
    defaultValues: {
      name: "",
      email: "",
      message: initialMessage,
      captcha: "",
    },
  });
  const [num1, setNum1] = useState(3);
  const [num2, setNum2] = useState(7);
  const [serverError, setServerError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const generateCaptcha = () => {
    setNum1(Math.floor(Math.random() * 20) + 1);
    setNum2(Math.floor(Math.random() * 20) + 1);
    resetField("captcha");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const onSubmitInternal: SubmitHandler<ContactMessage> = (c) => {
    setServerError("");
    console.info("submitting contact message...");

    return infoService
      .sendContact(c.name, c.email, c.message, c.captcha)
      .then(() => {
        trackGoogleAnalyticsEvent("generate_lead", { form_name: "contact_us" });
        setSubmitted(true);
      })
      .catch((err) => {
        console.error(err?.message ?? err);
        setServerError(t("contact.serverError"));
        generateCaptcha();
      });
  };

  const startNewMessage = () => {
    reset({
      name: "",
      email: "",
      message: "",
      captcha: "",
    });
    setServerError("");
    setSubmitted(false);
    generateCaptcha();
  };

  return (
    <Flex flex="1" align="center" justify="center" px={{ base: 4, md: 8, xl: 12 }} py={{ base: 6, md: 10 }} bg="gray.50">
      <Flex
        w="full"
        maxW="6xl"
        minH={{ base: "auto", lg: "680px" }}
        bg="white"
        borderWidth={{ base: "0", lg: "1px" }}
        borderColor="gray.200"
        rounded={{ base: "none", lg: "lg" }}
        boxShadow={{ base: "none", lg: "0 24px 70px rgba(15, 23, 42, 0.10)" }}
        overflow="hidden"
      >
          <Box
            flex="1.08"
            position="relative"
            display={{ base: "none", lg: "block" }}
            bg="gray.900"
          >
            <Image
              alt={t("contact.imageAlt")}
              src="/images/matterhorn.webp"
              w="full"
              h="full"
              minH="680px"
              objectFit="cover"
            />
            <Box position="absolute" inset="0" bg="linear-gradient(180deg, rgba(15, 23, 42, 0.05) 0%, rgba(15, 23, 42, 0.76) 100%)" />
            <Stack
              position="absolute"
              left="10"
              right="10"
              bottom="10"
              gap="4"
              color="white"
            >
              <Box w="12" h="1" bg="green.300" rounded="full" />
              <Heading fontSize="3xl" lineHeight="1.1" letterSpacing="0">
                {t("contact.heroTitle")}
              </Heading>
              <HStack gap="2" color="whiteAlpha.900">
                <Icon as={LuClock3} boxSize="5" />
                <Text fontSize="sm" fontWeight="medium">
                  {t("contact.heroText")}
                </Text>
              </HStack>
            </Stack>
          </Box>

          <Flex
            flex="1"
            align="center"
            justify="center"
            px={{ base: 0, sm: 6, md: 10, xl: 14 }}
            py={{ base: 4, md: 10 }}
          >
            {submitted ? (
              <Stack
                w="full"
                maxW="29rem"
                mx="auto"
                bg="white"
                borderWidth={{ base: "1px", lg: "0" }}
                borderColor="gray.200"
                rounded="lg"
                boxShadow={{ base: "0 16px 46px rgba(15, 23, 42, 0.08)", lg: "none" }}
                py={{ base: 7, md: 0 }}
                px={{ base: 5, sm: 8, md: 0 }}
                gap={{ base: 7, md: 8 }}
              >
                <HStack gap="3" align="center">
                  <Avatar fallback={<Icon as={LuCircleCheck} boxSize="6" />} bg="green.500" color="white" size="lg" />
                  <Stack gap="1">
                    <Heading fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1.08" letterSpacing="0">
                      {t("contact.successTitle")}
                    </Heading>
                    <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                      {t("contact.successStatus")}
                    </Text>
                  </Stack>
                </HStack>

                <Alert
                  status="success"
                  borderStartWidth="3px"
                  borderStartColor="green.500"
                  title={t("contact.successTitle")}
                >
                  {t("contact.successText")}
                </Alert>

                <Button
                  size="lg"
                  w="full"
                  variant="outline"
                  color="gray.700"
                  borderColor="gray.300"
                  _hover={{ bg: "gray.50", borderColor: "gray.400" }}
                  onClick={startNewMessage}
                >
                  <Icon as={LuMessageSquare} />
                  {t("contact.newMessage")}
                </Button>
              </Stack>
            ) : (
              <form noValidate onSubmit={handleSubmit(onSubmitInternal)} style={{ width: "100%" }}>
                <Fieldset.Root
                  size={{ base: "md", md: "lg" }}
                  invalid={serverError.length > 0}
                  w="full"
                  maxW="29rem"
                  mx="auto"
                  bg="white"
                  borderWidth={{ base: "1px", lg: "0" }}
                  borderColor="gray.200"
                  rounded="lg"
                  boxShadow={{ base: "0 16px 46px rgba(15, 23, 42, 0.08)", lg: "none" }}
                  py={{ base: 7, md: 0 }}
                  px={{ base: 5, sm: 8, md: 0 }}
                >
                  <Stack gap={{ base: 7, md: 8 }}>
                    <HStack gap="3" align="center">
                      <Avatar fallback={<Icon as={LuMessageSquare} boxSize="6" />} bg="blue.500" color="white" size="lg" />
                      <Stack gap="1">
                        <Heading fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1.08" letterSpacing="0">
                          {t("contact.title")}
                        </Heading>
                        <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                          {t("contact.subtitle")}
                        </Text>
                      </Stack>
                    </HStack>

                    <Fieldset.Content gap="5">
                      {serverError && (
                        <Alert status="error" title={t("contact.errorTitle")}>
                          {serverError}
                        </Alert>
                      )}

                      <Field id="name" label={t("contact.name")} required invalid={!!errors.name} errorText={errors.name?.message}>
                        <InputGroup w="full" startElement={<Icon as={LuUserRound} color="gray.500" />}>
                          <Input
                            autoFocus
                            autoComplete="name"
                            type="text"
                            placeholder={t("contact.namePlaceholder")}
                            {...register("name", { required: t("contact.nameRequired") })}
                          />
                        </InputGroup>
                      </Field>

                      <Field id="email" label={t("contact.email")} required invalid={!!errors.email} errorText={errors.email?.message}>
                        <InputGroup w="full" startElement={<Icon as={LuMail} color="gray.500" />}>
                          <Input
                            autoComplete="email"
                            type="email"
                            placeholder={t("contact.emailPlaceholder")}
                            {...register("email", { required: t("contact.emailRequired") })}
                          />
                        </InputGroup>
                      </Field>

                      <Field id="message" label={t("contact.message")} required invalid={!!errors.message} errorText={errors.message?.message}>
                        <Textarea
                          minH="10rem"
                          resize="vertical"
                          placeholder={t("contact.messagePlaceholder")}
                          {...register("message", { required: t("contact.messageRequired") })}
                        />
                      </Field>

                      <CaptchaInput
                        leftOperand={num1}
                        rightOperand={num2}
                        onRegenerate={generateCaptcha}
                        invalid={!!errors.captcha}
                        errorText={errors.captcha?.message}
                        {...register("captcha", {
                          required: t("contact.captchaRequired"),
                          validate: (value) =>
                            parseInt(value, 10) === num1 + num2 || t("contact.captchaInvalid"),
                        })}
                      />

                      <Button
                        type="submit"
                        loading={isSubmitting}
                        loadingText={t("contact.sending")}
                        size="lg"
                        w="full"
                        bg="blue.500"
                        color="white"
                        _hover={{ bg: "blue.600" }}
                      >
                        <Icon as={LuSend} />
                        {t("contact.submit")}
                      </Button>
                    </Fieldset.Content>
                  </Stack>
                </Fieldset.Root>
              </form>
            )}
          </Flex>
      </Flex>
    </Flex>
  );
};

export default Contact;
